<script setup lang="ts">
import { ref, computed, nextTick, onMounted, onBeforeUnmount, watch } from 'vue'
import { CubeIcon } from '@heroicons/vue/24/outline'
import { Display3DModelDto, type Display3DModel } from 'monocle-models'
import { orbitParamsFromEulerAngles, deriveProperty } from '../helpers/core'

const props = defineProps({
  bundleId: {
    type: String,
    required: true,
  },
  shortcode: {
    type: String,
    default: undefined,
  },
  authToken: {
    type: String,
    default: undefined,
  },
  hideLogo: {
    type: Boolean,
    default: false,
  },
  wallMounted: {
    type: Boolean,
  },
  enableRotation: {
    type: Boolean,
    default: false,
  },
  rotationPerSecond: {
    type: String,
    default: '10deg',
  },
  enableInteractionPrompt: {
    type: Boolean,
    default: false,
  },
  interactionPromptStyle: {
    type: String,
    default: 'basic',
  },
  disableZoom: {
    type: Boolean,
    default: false,
  },
  arActualSize: {
    type: Boolean,
    default: false,
  },
  testMode: {
    type: Boolean,
    default: false,
  },
  darkMode: {
    type: Boolean,
    default: false,
  },
  fieldOfView: {
    type: String,
  },
  apiBaseUrl: {
    type: String,
  },
})

// Load Google Model Viewer
onMounted(() => {
  import('@google/model-viewer')
})

// State
const data = ref<Display3DModel | null>(null)
const error = ref<string | null>(null)
const progress = ref(0)
const finishedLoading = ref(false)
const arLoading = ref(false)
const modelViewerRef = ref<HTMLElement | null>(null)

// Request deduplication state
const pendingBundleId = ref<string | null>(null)
const refreshInterval = ref<NodeJS.Timeout | null>(null)

// Realtime connection state
const sseConnected = ref(false)
const sseError = ref<string | null>(null)
const bundleWebSocket = ref<WebSocket | null>(null)
const bundleWebSocketGeneration = ref(0)

// iOS detection
const isIOS = computed(() => {
  if (typeof navigator === 'undefined') return false
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  )
})

// AR activation using model-viewer's native method
const activateAR = async () => {
  if (!modelViewerRef.value) return

  arLoading.value = true

  try {
    const modelViewer = modelViewerRef.value as any

    // Use model-viewer's native activateAR method
    if (typeof modelViewer.activateAR === 'function') {
      await modelViewer.activateAR()
    }
  } catch (error) {
    console.error('Failed to activate AR:', error)
  } finally {
    // Reset loading state after a short delay
    setTimeout(() => {
      arLoading.value = false
    }, 1000)
  }
}

// AR Detection utility method - passthrough to model-viewer
const canActivateAR = async (): Promise<boolean> => {
  if (modelViewerRef.value && 'canActivateAR' in modelViewerRef.value) {
    const modelViewer = modelViewerRef.value as any
    return await modelViewer.canActivateAR
  }
  return false
}

// Determine API base URL
const getApiBaseUrl = () => {
  if (props.apiBaseUrl) return props.apiBaseUrl

  // Use the Gallery URL from vite.config.ts galleryUrl option
  // assert we have a valid URL
  if (!import.meta.env.VITE_GALLERY_URL) {
    throw new Error('VITE_GALLERY_URL is not defined in environment variables')
  }
  const galleryUrl = new URL(import.meta.env.VITE_GALLERY_URL)
  return galleryUrl + 'api'
}

const closeBundleWebSocket = () => {
  if (bundleWebSocket.value) {
    bundleWebSocketGeneration.value += 1
    bundleWebSocket.value.onclose = null
    bundleWebSocket.value.onerror = null

    if (bundleWebSocket.value.readyState === WebSocket.OPEN) {
      bundleWebSocket.value.close()
    }

    bundleWebSocket.value = null
  }
}

// WebSocket realtime connection with polling fallback.
const setupRealtimeConnection = (bundleId: string) => {
  closeBundleWebSocket()
  if (refreshInterval.value) {
    clearInterval(refreshInterval.value)
    refreshInterval.value = null
  }

  if (!bundleId) {
    data.value = null
    return
  }

  error.value = null
  sseError.value = null
  sseConnected.value = false

  try {
    const generation = bundleWebSocketGeneration.value + 1
    bundleWebSocketGeneration.value = generation
    const baseUrl = getApiBaseUrl()
    const url = new URL(`${baseUrl}/bundle/${bundleId}/ws`)
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:'

    if (props.shortcode) {
      url.searchParams.set('shortcode', props.shortcode)
    }
    if (props.authToken) {
      url.searchParams.set('token', props.authToken)
    }

    const ws = new WebSocket(url.toString())
    bundleWebSocket.value = ws

    ws.onopen = () => {
      if (bundleWebSocketGeneration.value !== generation) {
        ws.close()
        return
      }
      sseConnected.value = true
      sseError.value = null
    }

    ws.onmessage = (message) => {
      if (bundleWebSocketGeneration.value !== generation) return

      try {
        const parsed = JSON.parse(message.data)
        if (parsed.type === 'heartbeat') {
          return
        }
        if (parsed.type === 'deleted') {
          error.value = 'Bundle no longer available'
          data.value = null
          return
        }
        if (parsed.type === 'access_denied') {
          error.value = 'Access denied'
          data.value = null
          return
        }
        if (parsed.type === 'processing') {
          error.value = 'Bundle is processing'
          data.value = null
          return
        }
        if (parsed.type === 'error') {
          sseError.value =
            parsed.data?.message ?? parsed.data?.error ?? 'Connection error'
          return
        }
        const modelDto = Display3DModelDto.safeParse(parsed.data)
        if (modelDto.success) {
          error.value = null
          sseError.value = null
          data.value = modelDto.data
        }
      } catch (err) {
        console.error('Error parsing bundle WebSocket data:', err)
        sseError.value = 'Failed to parse bundle data'
      }
    }

    ws.onerror = () => {
      if (bundleWebSocketGeneration.value !== generation) return
      sseConnected.value = false
      sseError.value = 'Connection error'
    }

    ws.onclose = () => {
      if (bundleWebSocketGeneration.value !== generation) return
      if (bundleWebSocket.value !== ws) return
      bundleWebSocket.value = null
      sseConnected.value = false
      setupPolling(bundleId)
    }
  } catch (err) {
    console.error('Failed to setup bundle WebSocket connection:', err)
    sseError.value = 'Failed to establish connection'
    sseConnected.value = false
    setupPolling(bundleId)
  }
}

// Polling setup (fallback method)
const setupPolling = (bundleId: string) => {
  if (refreshInterval.value) {
    clearInterval(refreshInterval.value)
    refreshInterval.value = null
  }

  // Initial fetch
  fetchBundleData(bundleId)

  // Set up periodic refresh
  refreshInterval.value = setInterval(() => {
    fetchBundleData(bundleId)
  }, 30000)
}

// Fetch bundle data from API
const fetchBundleData = async (bundleId: string) => {
  // Skip if same request already in progress
  if (pendingBundleId.value && pendingBundleId.value === bundleId) {
    return
  }

  pendingBundleId.value = bundleId

  // Exit early if there's actually no bundle to fetch
  if (!bundleId) {
    data.value = null
    return
  }

  error.value = null

  try {
    const baseUrl = getApiBaseUrl()
    const urlObj = new URL(`${baseUrl}/bundle/${bundleId}`)
    if (props.shortcode) {
      urlObj.searchParams.set('shortcode', props.shortcode)
    }

    const headers: Record<string, string> = {}
    if (props.authToken) {
      headers['Authorization'] = `Bearer ${props.authToken}`
    }

    const response = await fetch(urlObj.toString(), { headers })

    if (!response.ok) {
      throw new Error(
        `Failed to fetch bundle: ${response.status} ${response.statusText}`,
      )
    }

    const bundleData = await response.json()
    const modelDto = Display3DModelDto.safeParse(bundleData)
    if (modelDto.success) {
      sseError.value = null
      data.value = modelDto.data
    }
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to load bundle'
    console.error('Error fetching bundle:', err)
    data.value = null
  } finally {
    pendingBundleId.value = null
  }
}

// Initialize data - watch for bundleId changes and use WebSocket with polling fallback
watch(
  () => props.bundleId,
  (newBundleId, oldBundleId) => {
    if (newBundleId && newBundleId !== oldBundleId) {
      setupRealtimeConnection(newBundleId)
    }
  },
  { immediate: true },
)

// Clean up connections on unmount
onBeforeUnmount(() => {
  if (refreshInterval.value) {
    clearInterval(refreshInterval.value)
    refreshInterval.value = null
  }
  closeBundleWebSocket()
})

// Progress tracking
const updateProgress = (event: CustomEvent) => {
  progress.value = event.detail.totalProgress
}

const modelVisibility = (event: CustomEvent) => {
  if (event.detail.visible) {
    finishedLoading.value = true
  }
}

// Consolidated computed property that rolls up all model-viewer state
const presentedData = computed(() => {
  // Return null if we don't have the core data yet
  if (!data.value) {
    return null
  }

  const wallMountedSetting = deriveProperty(
    undefined, // no URL params in component
    props.wallMounted,
    data.value.options?.wall_mounted,
  )

  const autoRotateSetting = (() => {
    if (props.testMode) {
      return null
    } else if (wallMountedSetting) {
      return null // Disable rotation for wall mounted
    } else if (props.enableRotation) {
      return true
    } else {
      return null
    }
  })()

  const disableZoomSetting = (() => {
    if (props.testMode) {
      return true
    } else if (props.disableZoom) {
      return true
    } else {
      return null
    }
  })()

  const cameraOrbitParams = data.value.options?.camera_euler_angles
    ? orbitParamsFromEulerAngles(data.value.options.camera_euler_angles)
    : ''

  const showPoster = progress.value < 1

  return {
    // Core data
    glbUrl: data.value.glb_url,
    usdzUrl: data.value.usdz_url,
    posterJpgDataUrl: data.value.poster_jpg_data_url,
    posterMaskPngDataUrl: data.value.poster_mask_png_data_url,

    // Camera settings
    cameraOrbit: cameraOrbitParams,
    fieldOfView: props.fieldOfView,
    maxCameraOrbit: wallMountedSetting ? '45deg 120deg auto' : 'auto auto auto',
    minCameraOrbit: wallMountedSetting ? '-45deg 60deg auto' : 'auto auto auto',

    // Behavior settings
    autoRotate: autoRotateSetting,
    disableZoom: disableZoomSetting,
    arPlacement: wallMountedSetting ? 'wall' : 'floor',
    interactionPrompt: props.testMode
      ? 'none'
      : props.enableInteractionPrompt
        ? 'auto'
        : 'none',
    interactionPromptStyle: props.interactionPromptStyle,

    // Display flags
    showPoster: showPoster,

    // Meta information
    title: data.value.info?.title || '',
    description: data.value.info?.description || '',

    // Settings
    wallMounted: wallMountedSetting,
  }
})

const modelViewerKey = computed(() => {
  if (!data.value) return ''

  const glbRevision = data.value.glb_metadata?.md5 ?? ''
  const usdzRevision = data.value.usdz_metadata?.md5 ?? ''

  return [glbRevision, usdzRevision].join(':')
})

watch(modelViewerKey, (newKey, oldKey) => {
  if (!oldKey || newKey === oldKey) return

  progress.value = 0
  finishedLoading.value = false
})

watch(
  () => presentedData.value?.wallMounted,
  async (wallMounted, previousWallMounted) => {
    if (!wallMounted || wallMounted === previousWallMounted) return

    await nextTick()

    const modelViewer = modelViewerRef.value as
      | (HTMLElement & {
          autoRotate?: boolean
          jumpCameraToGoal?: () => void
          resetTurntableRotation?: (theta?: number) => void
        })
      | null

    if (!modelViewer || !presentedData.value) return

    modelViewer.autoRotate = false
    modelViewer.removeAttribute('auto-rotate')
    modelViewer.resetTurntableRotation?.(0)
    modelViewer.setAttribute('camera-orbit', presentedData.value.cameraOrbit)
    modelViewer.jumpCameraToGoal?.()
  },
)

// Generate random ID for SVG mask
const randomString = () => {
  return Math.random().toString(36).substring(2, 6)
}

const posterMaskId = ref(`poster-mask-${randomString()}`)
const posterMaskLocalRefUrl = ref(`url(#${posterMaskId.value})`)

const ViewerStage = {
  GettingData: 'GettingData',
  ShowingError: 'ShowingError',
  LoadingModel: 'LoadingModel',
  ShowingModel: 'ShowingModel',
} as const

type ViewerStage = (typeof ViewerStage)[keyof typeof ViewerStage]

const effectiveError = computed(() => {
  return error.value ?? sseError.value ?? null
})

const viewerStage = computed<ViewerStage>(() => {
  if (effectiveError.value) {
    return ViewerStage.ShowingError
  }

  if (!presentedData.value) {
    return ViewerStage.GettingData
  }

  if (!finishedLoading.value) {
    return ViewerStage.LoadingModel
  }

  return ViewerStage.ShowingModel
})

// Meta information for head (available but not currently used)
// const title = computed(() => data.value?.info?.title || 'Monocle Viewer')
// const description = computed(() => data.value?.info?.description || '3D Model')

// Expose methods for external use
defineExpose({
  canActivateAR,
  activateAR,
})
</script>

<template>
  <div
    class="container"
    :class="{ 'dark-mode': darkMode }">
    <!-- Error state -->
    <div
      v-if="viewerStage === ViewerStage.ShowingError"
      class="error-state">
      Error loading bundle: {{ effectiveError }}
    </div>

    <!-- Loading state -->
    <div
      v-else-if="viewerStage === ViewerStage.GettingData"
      class="loading-state">
      Loading...
    </div>

    <!-- Main viewer - LoadingModel or ShowingModel -->
    <div
      v-else
      class="viewer-content"
      :class="{
        'viewer-loading-model': viewerStage === ViewerStage.LoadingModel,
        'viewer-showing-model': viewerStage === ViewerStage.ShowingModel,
      }">
      <!-- Loading UI -->
      <div
        v-if="viewerStage === ViewerStage.LoadingModel"
        class="loading-model">
        <div id="progress">
          <div
            class="bar"
            :style="{ transform: 'scaleX(' + progress + ')' }"></div>
        </div>

        <!-- Poster image during loading -->
        <div
          class="poster-container"
          v-if="
            presentedData &&
            presentedData.showPoster &&
            presentedData.posterJpgDataUrl
          ">
          <div
            v-if="!presentedData.posterMaskPngDataUrl"
            class="lazy-load-poster">
            <img
              class="poster-image"
              :src="presentedData.posterJpgDataUrl"
              alt="Model poster" />
          </div>
          <div
            v-else
            class="lazy-load-poster">
            <svg
              class="poster-svg"
              preserveAspectRatio="xMidYMid"
              version="1.1"
              xmlns="http://www.w3.org/2000/svg"
              xmlns:xlink="http://www.w3.org/1999/xlink"
              viewBox="0 0 640 480">
              <defs>
                <mask :id="posterMaskId">
                  <image
                    width="640"
                    height="480"
                    :href="presentedData.posterMaskPngDataUrl"></image>
                </mask>
              </defs>
              <image
                width="640"
                height="480"
                :href="presentedData.posterJpgDataUrl"
                :mask="posterMaskLocalRefUrl"></image>
            </svg>
          </div>
        </div>
      </div>

      <!-- Model viewer -->
      <model-viewer
        v-if="presentedData"
        :key="modelViewerKey"
        ref="modelViewerRef"
        class="google-model-viewer"
        tabindex="-1"
        @focus="modelViewerRef?.blur()"
        ar
        ar-scale="auto"
        ar-modes="webxr scene-viewer"
        min-field-of-view="10deg"
        max-field-of-view="150deg"
        :disable-zoom="presentedData.disableZoom"
        :ar-placement="presentedData.arPlacement"
        camera-controls
        disable-tap
        :auto-rotate="presentedData.autoRotate"
        auto-rotate-delay="0"
        :rotation-per-second="rotationPerSecond"
        :field-of-view="presentedData.fieldOfView"
        :src="presentedData.glbUrl"
        :ios-src="presentedData.usdzUrl"
        :camera-orbit="presentedData.cameraOrbit"
        :max-camera-orbit="presentedData.maxCameraOrbit"
        :min-camera-orbit="presentedData.minCameraOrbit"
        :interaction-prompt="presentedData.interactionPrompt"
        :interaction-prompt-style="presentedData.interactionPromptStyle"
        @progress="updateProgress"
        @model-visibility="modelVisibility"
        shadow-intensity="1"
        exposure="1">
      </model-viewer>

      <!-- Custom AR button for iOS -->
      <div
        v-if="
          presentedData && isIOS && presentedData.usdzUrl && finishedLoading
        "
        class="custom-ar-button"
        @click="activateAR">
        <div
          class="ar-icon"
          v-if="!arLoading">
          <CubeIcon />
        </div>
        <div
          class="ar-loading"
          v-if="arLoading">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg">
            <circle
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="2"
              fill="none"
              opacity="0.3" />
            <path
              d="M12 2A10 10 0 0 1 22 12"
              stroke="currentColor"
              stroke-width="2"
              fill="none">
              <animateTransform
                attributeName="transform"
                type="rotate"
                dur="1s"
                values="0 12 12;360 12 12"
                repeatCount="indefinite" />
            </path>
          </svg>
        </div>
      </div>

      <!-- Logo -->
      <div
        v-if="!hideLogo"
        class="logo-container">
        <a href="https://steampunk.digital">
          <img
            alt="Steampunk Digital Logo"
            src="../assets/SPDLogo.png" />
        </a>
      </div>

      <!-- Test finished loading indicator -->
      <div
        v-if="finishedLoading"
        id="test-finished-loading"
        data-testid="finished-loading"></div>
    </div>
  </div>
</template>

<style>
@import '../assets/globals.css';
</style>

<style scoped>
div {
  line-height: 0px;
}

#test-finished-loading {
  width: 0;
  height: 0;
  display: hidden;
}

.container {
  position: relative;
  width: 100%;
  height: 100%;

  /* Disable text selection (including double-tap + drag) */
  user-select: none;
  -webkit-user-select: none;

  /* Disable the long-press callout (copy/paste menu) */
  -webkit-touch-callout: none;

  /* Remove the gray tap highlight when you touch the element */
  -webkit-tap-highlight-color: transparent;
}

.error-state,
.loading-state {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  font-family: Arial, sans-serif;
  color: #666;
}

.viewer-content {
  position: relative;
  width: 100%;
  height: 100%;
}

model-viewer {
  width: 100%;
  height: 100%;
}

#progress {
  position: absolute;
  height: 10px;
  width: 200px;
  color: rgba(220, 220, 220, 0.75);
  box-shadow: 0px 0px 8px 6px rgba(0, 0, 0, 0.25);
  border: 2px solid currentColor;
  border-radius: 10px;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  opacity: 1;
  transition: opacity 0.3s 0.3s;
  pointer-events: none;
  z-index: 2;
}

.bar {
  width: 100%;
  height: 100%;
  background-color: currentColor;
  transform-origin: top left;
  transform: scaleX(0);
  opacity: 1;
}

.poster-container {
  position: absolute;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 1;
}

.poster-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.lazy-load-poster {
  background-color: transparent;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.poster-svg {
  width: 100%;
  height: 100%;
}

.logo-container {
  z-index: 999;
  position: absolute;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.logo-container img {
  height: 1.5rem;
  bottom: 10px;
  left: 10px;
  margin: 4px;
  position: absolute;
  pointer-events: all;
}

/* Hide default progress bar */
model-viewer::part(default-progress-bar) {
  display: none;
}

/* Custom AR button for iOS */
.custom-ar-button {
  position: absolute;
  bottom: 20px;
  right: 20px;
  width: 24px;
  height: 24px;
  padding: 4px;
  background-color: var(--background_8);
  color: var(--text-color);
  border: 1px solid var(--background-reverse);
  border-radius: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 100;
  transition: all 0.2s ease;
}

.custom-ar-button:active {
  transform: scale(0.95);
}

.ar-loading svg {
  stroke: var(--background-reverse);
  display: block;
}

.ar-icon svg {
  stroke: var(--background-reverse);
}

.ar-icon {
  stroke: var(--background-reverse);
  display: block;
  width: 24px;
  height: 24px;
}

/* Hide model-viewer AR button on iOS */
@media (max-width: 768px) {
  model-viewer::part(default-ar-button) {
    display: none !important;
  }
}
</style>
